import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, XIcon, SmartphoneIcon } from 'lucide-react';
import { usePwaInstall } from '../../app/hooks/usePwaInstall';
import { DjayAvatar } from '../../mascot';
/**
 * InstallAppPrompt — Vriendelijke, dismissible banner die verschijnt wanneer
 * Chrome/Edge de site als PWA wil installeren.
 *
 * - Verschijnt rechtsonder op desktop, en als bottom-sheet op mobile.
 * - Toont Djay met een vriendelijke uitnodiging.
 * - Gebruiker kan installeren (native prompt) of wegklikken (14 dagen stil).
 * - Werkt alleen op de gepubliceerde HTTPS-site — niet in de sandbox preview.
 */
export function InstallAppPrompt() {
  const {
    canInstall,
    promptInstall,
    dismiss
  } = usePwaInstall();
  return <AnimatePresence data-id="element-2363">
      {canInstall && <motion.div key="install-prompt" initial={{
      y: 40,
      opacity: 0,
      scale: 0.96
    }} animate={{
      y: 0,
      opacity: 1,
      scale: 1
    }} exit={{
      y: 40,
      opacity: 0,
      scale: 0.96
    }} transition={{
      duration: 0.3,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="fixed z-[55] bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-sm" role="dialog" aria-labelledby="pwa-install-title" aria-describedby="pwa-install-desc" data-id="element-2364">
          <div className="relative bg-white border border-orange-100 rounded-2xl shadow-xl p-4 md:p-5 overflow-hidden" data-id="element-2365">
            {/* Dismiss knop */}
            <button type="button" onClick={dismiss} aria-label="Sluit installatie-melding" className="absolute top-2 right-2 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300" data-id="element-2366">
              <XIcon className="w-4 h-4" aria-hidden="true" data-id="element-2367" />
            </button>

            <div className="flex items-start gap-3 pr-6" data-id="element-2368">
              {/* Djay */}
              <div className="flex-shrink-0" data-id="element-2369">
                <DjayAvatar size="sm" emotion="excited" data-id="element-2370" />
              </div>

              {/* Tekst */}
              <div className="flex-1 min-w-0 pt-1" data-id="element-2371">
                <h3 id="pwa-install-title" className="text-sm font-extrabold text-slate-900 leading-tight" data-id="element-2372">
                  Installeer Meester Djay
                </h3>
                <p id="pwa-install-desc" className="mt-1 text-xs text-slate-600 leading-snug" data-id="element-2373">
                  Zet me als app op je startscherm — sneller starten, geen
                  browser-balk, en altijd dichtbij.
                </p>
              </div>
            </div>

            {/* Knoppen */}
            <div className="mt-4 flex items-center gap-2" data-id="element-2374">
              <button type="button" onClick={() => void promptInstall()} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2" data-id="element-2375">
                <DownloadIcon className="w-4 h-4" aria-hidden="true" data-id="element-2376" />
                Installeer app
              </button>
              <button type="button" onClick={dismiss} className="px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300" data-id="element-2377">
                Later
              </button>
            </div>

            {/* Subtiele hint icoon strip */}
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400" data-id="element-2378">
              <SmartphoneIcon className="w-3 h-3" aria-hidden="true" data-id="element-2379" />
              <span data-id="element-2380">Werkt op telefoon, tablet en computer</span>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}